import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { createFragmentContainer, graphql } from 'react-relay';
import { useFormik } from 'formik';
import { omit } from 'ramda';
import { toast } from 'react-toastify';

import appRoutes from '../../../routes/appRoutes';
import { graphErrorsToFormik } from '../../../utils/errors';
import StorePresenter from '../../../presenters/StorePresenter';
import {
  initialValues,
  validationSchema,
} from '../../../forms/store/editStoreForm';
import Button from '../../../components/Button';
import FormHeader from '../../../components/FormHeader';
import StoreForm from '../../../components/StoreForm';
import { StoreProvider, Dialog } from '../../../components';
import { updateStore, archiveStore } from '../../../mutations';

export const BrandEditView = (props) => {
  const {
    brand,
    relay: { environment },
    lists,
    history,
  } = props;
  const categories = lists.categories.edges.map((e) => e.node);

  const notify = () =>
    toast.success('Brand Successfully Updated!', {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
    });

  const handleBrandUpdate = async (values, actions) => {
    const brandAttributes = omit(
      ['logoUrl', 'imageUrl', 'locationMapUrl', 'categories', 'state'],
      values
    );

    const params = {
      ...brandAttributes,
      phoneNumber: brandAttributes.phoneNumber.replace(/^\+?1/, ''),
    };

    actions.setSubmitting(true);
    await updateStore(
      environment,
      { id: StorePresenter.id(brand), ...params },
      {
        onCompleted: handleSubmitSuccess,
      }
    );

    actions.setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: initialValues(brand),
    onSubmit: handleBrandUpdate,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema,
  });

  const { values, handleSubmit, setFieldValue, errors, setErrors } = formik;

  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState(false);

  const handleSubmitSuccess = (response) => {
    const { errors } = response.updateStore;
    if (errors) {
      setErrors(graphErrorsToFormik(errors));
    } else {
      history.push(appRoutes.storeShowPath(StorePresenter.id(brand)));
      notify();
    }
  };

  const handleArchiveStore = () => {
    setIsDeletionDialogOpen(true);
  };

  const handleCancelDeletion = () => {
    setIsDeletionDialogOpen(false);
  };

  const handleConfirmDeletion = () => {
    setIsDeletionDialogOpen(false);

    archiveStore(environment, StorePresenter.id(brand), {
      onCompleted: () =>
        history.push(appRoutes.storeShowPath(StorePresenter.id(brand))),
      onError: (error) => setErrors({ error: `commit failed: ${error}` }),
    });
    toast.success('Brand Successfully Archived!', {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
    });
  };
  return (
    <div className="store-edit">
      <Dialog
        className="deletion"
        isOpen={isDeletionDialogOpen}
        message="This will archive this brand. Are you sure you want to proceed?"
        onConfirm={handleConfirmDeletion}
        onCancel={handleCancelDeletion}
      />
      <form className="store-form" onSubmit={handleSubmit}>
        <FormHeader title="Edit Brand">
          {brand.state !== 'archived' ? (
            <Button
              id="archiveStore"
              className="button delete"
              onClick={handleArchiveStore}
            >
              Archive Brand
            </Button>
          ) : null}
          <input
            id="saveStore"
            className="store-submit"
            type="submit"
            defaultValue="Save"
          />
        </FormHeader>

        <div className="form-fields">
          <StoreForm
            errors={errors}
            values={values}
            categories={categories}
            onChange={setFieldValue}
            storeId={StorePresenter.id(brand)}
          />
        </div>
      </form>
    </div>
  );
};

const BrandEditContainer = withRouter(
  createFragmentContainer(BrandEditView, {
    brand: graphql`
      fragment BrandEdit_brand on Store {
        id
        name
        phoneNumber
        email
        storeNumber
        defaultCashbackRate
        birthdayCashbackRate
        uniqueInState
        fastCashEnabled
        normalOpeningHour
        normalClosingHour
        sundayOpeningHour
        sundayClosingHour
        customerServiceEmail
        isFeatured
        logoUrl
        imageUrl
        locationMapUrl
        billingAddress
        state
        categories {
          id
          name
        }
      }
    `,
    lists: graphql`
      fragment BrandEdit_lists on Lists {
        id
        categories(first: 1000) {
          edges {
            node {
              id
              name
            }
          }
        }
      }
    `,
  })
);

const BrandEdit = () => {
  return (
    <StoreProvider
      modern
      container={BrandEditContainer}
      query={graphql`
        query BrandEditQuery($brandId: ID!) {
          lists {
            ...BrandEdit_lists
          }
          brand: store(id: $brandId) {
            ...BrandEdit_brand
          }
        }
      `}
    />
  );
};

export default BrandEdit;

BrandEditView.propTypes = {
  brand: StorePresenter.shape().isRequired,
  relay: PropTypes.shape().isRequired,
  lists: PropTypes.shape().isRequired,
  history: PropTypes.shape().isRequired,
};
